/**
 * ChimeChooser — the firm's uploaded chimes as a radio list with ▶ demos, and
 * the per-user pick (shadow_ui['chime_id']). Self-contained so it can live in
 * both the Settings panel and the Quiet modal without the two drifting.
 * (Ken 2026-10-21 — surfaced inside the Quiet modal.)
 */

import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Play } from 'lucide-react'
import { useAuth } from '../auth'
import { listChimeSounds, chimeSoundAudioUrl, getShadowUi, saveShadowUi } from '../api'

export default function ChimeChooser() {
  const auth = useAuth()
  const chimesQ = useQuery({ queryKey: ['chime-sounds'], queryFn: listChimeSounds, staleTime: 60_000 })
  const sounds = chimesQ.data ?? []
  const serverChime = (auth.status === 'authenticated' ? (auth.user.chime_id ?? null) : null)
  const [chimeId, setChimeId] = useState<number | null>(serverChime)
  const [saving, setSaving] = useState(false)
  useEffect(() => { setChimeId(serverChime) }, [serverChime])

  function demo(id: number) {
    try { const a = new Audio(chimeSoundAudioUrl(id)); a.volume = 0.6; void a.play().catch(() => {}) } catch { /* ignore */ }
  }
  async function pick(id: number) {
    if (id === chimeId) return
    const prev = chimeId
    setChimeId(id)              // optimistic
    setSaving(true)
    try {
      const cur = await getShadowUi()
      await saveShadowUi({ ...cur, chime_id: id })
      auth.refresh()
    } catch {
      setChimeId(prev)
    } finally {
      setSaving(false)
    }
  }

  if (sounds.length === 0) {
    return <div className="text-[11px] me-text-mute">No chimes uploaded yet.</div>
  }
  return (
    <div className="flex flex-col gap-0.5">
      {sounds.map((s) => (
        <div key={s.id} className="flex items-center gap-1.5">
          <button onClick={() => demo(s.id)} title="Hear it"
            className="shrink-0 me-text-mute hover:me-text"><Play size={11} /></button>
          <button
            onClick={() => pick(s.id)}
            disabled={saving}
            data-selected={chimeId === s.id ? 'true' : undefined}
            className="flex-1 truncate rounded px-1.5 py-0.5 text-left text-[12px] me-text data-[selected]:bg-[color:var(--me-selected)]"
          >
            {s.name}
          </button>
        </div>
      ))}
    </div>
  )
}
